import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import '../styles/FAQ.css';

interface FAQItem {
  q: string;
  a: string;
}

const FAQS: FAQItem[] = [
  {
    q: 'How is this different from hiring an AI consultant?',
    a: 'Most consultants ship a deck. We ship working agents that run in production with observability, guardrails, and a paid retainer for tuning. You own the code and the data—forever.',
  },
  {
    q: 'How long until something is live?',
    a: 'A focused first agent typically goes live within 30 days from kickoff. We start in shadow mode, move to human-approved actions, then graduate to autonomous once the metrics earn it.',
  },
  {
    q: 'Will the agent break things?',
    a: 'Every action is reversible, audit-logged, and gated by guardrails sized for the blast radius. Critical operations require human approval. We treat agents like any other production system.',
  },
  {
    q: 'What does a typical engagement look like?',
    a: 'A scoped pilot (4–6 weeks, fixed fee) for the first agent, followed by a monthly retainer to expand scope, monitor, and tune. No marathon contracts—we earn renewal each quarter.',
  },
  {
    q: 'Do you work with our existing stack?',
    a: 'Yes. Our agents bolt onto whatever you already use—HubSpot, Salesforce, Zendesk, Snowflake, Notion, internal APIs. If it has an API or a database, we can talk to it.',
  },
  {
    q: 'What about data security?',
    a: 'Single-tenant deployments by default. SOC 2-aligned controls. Models can run on your cloud (AWS/GCP/Azure) or via private inference endpoints. We sign DPAs and BAAs as needed.',
  },
  {
    q: 'What if we want to bring it in-house later?',
    a: 'Encouraged. Our handoff includes the codebase, model artefacts, runbooks, and—if useful—training for your engineers. The goal is to make ourselves replaceable.',
  },
];

const FAQ: React.FC = () => {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section id="faq" className="faq section-padding">
      <div className="container">
        <div className="section-kicker reveal">
          <span className="eyebrow">FAQ</span>
          <h2>Questions we get on every first call.</h2>
          <p>
            Short, honest answers. If we don&rsquo;t cover yours, ping{' '}
            <a href="mailto:hello@datryx.co">hello@datryx.co</a>—we reply same day.
          </p>
        </div>

        <div className="faq-list reveal">
          {FAQS.map((item, idx) => {
            const isOpen = open === idx;
            return (
              <div key={idx} className={`faq-item ${isOpen ? 'open' : ''}`}>
                <button
                  className="faq-question"
                  onClick={() => setOpen(isOpen ? null : idx)}
                  aria-expanded={isOpen}
                >
                  <span>{item.q}</span>
                  <Plus className="faq-icon" size={18} />
                </button>
                <div className="faq-answer-wrap">
                  <p className="faq-answer">{item.a}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
